// In-DOM confirmation modal. Resolves true on confirm, false on cancel,
// Escape, or backdrop click. We deliberately avoid window.confirm(): native
// dialogs block the browser-automation extension (and the event loop), and
// can't be asserted against in jsdom. This is the reusable security-action
// confirm used by the Devices & sessions screen.
//
// It optionally carries a §6.8 typed reason (`reason`): a required, length-
// capped textarea rendered above the actions. When present the promise resolves
// with the trimmed reason STRING on confirm (never empty when required) and null
// on dismiss — so a caller distinguishes "confirmed with reason R" from
// "cancelled". This is the one shared reason-carrying confirm every audited
// mutation reuses (invite resend/revoke and the AT2 successors), so the reason
// UX lives here rather than being re-authored per page.

import { t } from '../i18n';

// ReasonSpec configures the optional typed-reason textarea. `maxLength` mirrors
// the server's reason cap so an over-long reason fails locally rather than
// round-tripping to a 400; requiredError/tooLongError let the caller supply
// surface-specific copy (defaults fall back to the shared dialog strings).
export interface ReasonSpec {
  label: string;
  placeholder?: string;
  required?: boolean; // default true
  maxLength?: number;
  requiredError?: string;
  tooLongError?: string;
}

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  // 'danger' tints the confirm button with the error color for destructive
  // actions (revoke / sign out); 'default' uses the accent.
  tone?: 'default' | 'danger';
  // When set, the dialog collects a typed reason and resolves with the string
  // (see file header); without it the dialog is the plain boolean confirm.
  reason?: ReasonSpec;
}

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

// Per-instance id suffix (the FormScaffold nextId pattern). The ids used to be
// fixed strings, so two overlays in the DOM at once duplicated every one of them:
// the visible textarea ended up unlabeled and the visible dialog announced the
// OTHER dialog's title, because aria-labelledby resolves to the first match in
// document order.
let uid = 0;

// Single-flight latch. A double-clicked destructive trigger used to stack two
// overlays; confirming then resolved one promise and left the other dialog live,
// and the two focus traps fought over document.activeElement. A queue would be
// over-engineering for a double-click — the second call just reports "dismissed".
let openDialogs = 0;

// A reason-carrying confirm resolves the trimmed reason on confirm, null on
// dismiss; a plain confirm resolves true/false. The overloads keep every
// existing boolean caller unchanged while giving reason callers a string result.
export function confirmDialog(opts: ConfirmOptions & { reason: ReasonSpec }): Promise<string | null>;
export function confirmDialog(opts: ConfirmOptions): Promise<boolean>;
export function confirmDialog(opts: ConfirmOptions): Promise<boolean | string | null> {
  const previouslyFocused = document.activeElement as HTMLElement | null;
  const reasonSpec = opts.reason;
  // The "dismissed" value: null when a reason is expected (so it is distinct from
  // an empty reason), false for the plain boolean confirm.
  const dismissValue: boolean | null = reasonSpec ? null : false;

  // AC.2 — refuse to stack. Reported as a dismissal rather than a rejection so
  // every existing caller's `if (!await confirmDialog(...)) return;` shape keeps
  // working: a confirm the user never saw must not read as consent.
  if (openDialogs > 0) return Promise.resolve(dismissValue);

  uid += 1;
  const instance = uid;

  const overlay = document.createElement('div');
  overlay.className = 'confirm';
  overlay.dataset.tone = opts.tone ?? 'default';

  const dialog = document.createElement('div');
  dialog.className = 'confirm__dialog';
  dialog.setAttribute('role', 'alertdialog');
  dialog.setAttribute('aria-modal', 'true');

  const titleId = `confirm-title-${instance}`;
  const descId = `confirm-desc-${instance}`;
  dialog.setAttribute('aria-labelledby', titleId);
  dialog.setAttribute('aria-describedby', descId);

  const title = document.createElement('h2');
  title.className = 'confirm__title';
  title.id = titleId;
  title.textContent = opts.title;

  const message = document.createElement('p');
  message.className = 'confirm__message';
  message.id = descId;
  message.textContent = opts.message;

  dialog.append(title, message);

  // The optional typed-reason field. It sits between the message and the actions
  // so the reason reads as part of the confirmation, and is validated on confirm
  // (required + capped) with an inline error rather than a page-level one.
  let reasonInput: HTMLTextAreaElement | null = null;
  let reasonError: HTMLElement | null = null;
  if (reasonSpec) {
    const field = document.createElement('div');
    field.className = 'confirm__reason';

    const reasonId = `confirm-reason-${instance}`;
    const errId = `confirm-reason-error-${instance}`;

    const label = document.createElement('label');
    label.className = 'confirm__reason-label';
    label.htmlFor = reasonId;
    label.textContent = reasonSpec.label;

    const input = document.createElement('textarea');
    input.id = reasonId;
    input.className = 'confirm__reason-input';
    input.rows = 3;
    if (reasonSpec.placeholder) input.placeholder = reasonSpec.placeholder;
    if (reasonSpec.required ?? true) input.setAttribute('aria-required', 'true');
    input.setAttribute('aria-describedby', errId);

    const error = document.createElement('p');
    error.className = 'confirm__reason-err';
    error.id = errId;
    error.setAttribute('role', 'alert');
    error.hidden = true;

    field.append(label, input, error);
    dialog.append(field);
    reasonInput = input;
    reasonError = error;
    // Clear the error as soon as the user starts correcting it.
    input.addEventListener('input', () => {
      if (error.hidden) return;
      error.hidden = true;
      error.textContent = '';
      input.removeAttribute('aria-invalid');
    });
  }

  const actions = document.createElement('div');
  actions.className = 'confirm__actions';

  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'btn btn--secondary confirm__btn--cancel';
  cancel.textContent = opts.cancelLabel ?? t('common.cancel');

  const confirm = document.createElement('button');
  confirm.type = 'button';
  // Danger tone rides the shared solid variant (issue 253). The old
  // per-surface treatment in dialog.css overrode only the BACKGROUND under
  // .btn--primary, leaving dark ink (--brand-ink, right for a gold fill) on
  // the deep error fill — 4.65:1 at rest, 3.06:1 on hover, under AA.
  // .btn--danger--solid is white on a constant --color-error-strong (5.38:1
  // in every state, both themes; hover signals via the border, never a
  // darker fill). .confirm__btn--confirm stays on both branches — it is the
  // behavioural hook the tests and close-handlers select on.
  confirm.className =
    opts.tone === 'danger'
      ? 'btn btn--danger--solid confirm__btn--confirm'
      : 'btn btn--primary confirm__btn--confirm';
  confirm.textContent = opts.confirmLabel ?? t('dialog.confirm');

  actions.append(cancel, confirm);
  dialog.append(actions);
  overlay.append(dialog);

  openDialogs += 1;

  return new Promise<boolean | string | null>((resolve) => {
    let settled = false;

    function close(result: boolean | string | null): void {
      if (settled) return;
      settled = true;
      openDialogs -= 1;
      document.removeEventListener('keydown', onKeydown, true);
      overlay.remove();
      // Restore focus to whatever launched the dialog so keyboard users
      // land back on the triggering control.
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
      resolve(result);
    }

    // onConfirm validates the reason (when present) before closing, so a required
    // or over-long reason keeps the dialog open with an inline error instead of
    // resolving. A reasonless dialog just confirms.
    function onConfirm(): void {
      if (!reasonSpec || !reasonInput || !reasonError) {
        close(true);
        return;
      }
      const value = reasonInput.value.trim();
      const required = reasonSpec.required ?? true;
      if (required && value === '') {
        showReasonError(reasonSpec.requiredError ?? t('dialog.reasonRequired'));
        return;
      }
      if (reasonSpec.maxLength !== undefined && value.length > reasonSpec.maxLength) {
        showReasonError(reasonSpec.tooLongError ?? t('dialog.reasonTooLong'));
        return;
      }
      close(value);
    }

    function showReasonError(msg: string): void {
      if (!reasonInput || !reasonError) return;
      reasonError.textContent = msg;
      reasonError.hidden = false;
      reasonInput.setAttribute('aria-invalid', 'true');
      reasonInput.focus();
    }

    function onKeydown(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        close(dismissValue);
        return;
      }
      if (e.key === 'Tab') {
        const focusables = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const activeEl = document.activeElement as HTMLElement | null;
        if (e.shiftKey && activeEl === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && activeEl === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    cancel.addEventListener('click', () => close(dismissValue));
    confirm.addEventListener('click', onConfirm);
    overlay.addEventListener('click', (e) => {
      // Backdrop click (outside the dialog) cancels; clicks inside don't.
      if (e.target === overlay) close(dismissValue);
    });
    document.addEventListener('keydown', onKeydown, true);

    document.body.append(overlay);
    // When a reason is required, focus it so the user can type immediately;
    // otherwise default to Cancel — the safe choice for destructive confirms.
    if (reasonInput) reasonInput.focus();
    else cancel.focus();
  });
}
